import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  LoadingState,
  StatePanel,
  V2Badge,
  V2Button,
  V2DataGrid,
  V2EmptyState,
} from '@sakhtar/design-system/react';
import { tenantBasePath } from '../../lib/tenant.js';
import type { CrmLabel, CustomerListResponse, CustomerSummary } from '../../lib/types/customers.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { PartyLink } from './PartyLink.js';
import { CustomerCreateDialog } from './CustomerCreateDialog.js';
import { LabelChips } from './LabelChips.js';

const KIND_LABEL = { person: 'شخص', organization: 'سازمان' } as const;

export function CustomersPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [params, setParams] = useSearchParams();
  const [refresh, setRefresh] = useState(0);
  const [showCreate, setShowCreate] = useState(false);

  const query = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const labelId = params.get('label') ?? '';
  const sort = params.get('sort') ?? 'code';
  const order = params.get('order') ?? 'asc';
  const offset = Math.max(0, Number(params.get('offset') ?? 0) || 0);
  const limit = 25;
  const apiParams = new URLSearchParams({
    sort,
    order,
    offset: String(offset),
    limit: String(limit),
  });
  if (query) apiParams.set('search', query);
  if (status) apiParams.set('status', status);
  if (labelId) apiParams.set('labelId', labelId);
  const customers = useApiGet<CustomerListResponse>(
    slug ? `${base}/api/v1/crm/customers?${apiParams.toString()}` : null,
    [slug, query, status, labelId, sort, order, offset, refresh],
  );
  // The label filter offers the tenant's labels. A user who may not read them just gets no filter.
  const labelCatalogue = useApiGet<{ readonly items: readonly CrmLabel[] }>(
    slug ? `${base}/api/v1/crm/labels?status=active` : null,
    [slug, refresh],
  );
  const labelOptions = labelCatalogue.status === 'ready' ? labelCatalogue.data.items : [];

  if (!slug) return null;

  function patchParams(patch: Record<string, string | null>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (!value) next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  }

  const pageEnd = customers.status === 'ready' ? offset + customers.data.items.length : offset;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>مشتریان</h1>
          <div style={{ marginTop: 5, fontSize: 13, color: 'var(--sk-fg-muted)' }}>
            فقط طرف‌حساب‌های دارای نقش فعال مشتری
          </div>
        </div>
        <V2Button variant="primary" icon="plus" onClick={() => setShowCreate(true)}>
          مشتری جدید
        </V2Button>
      </div>

      <CustomerCreateDialog
        open={showCreate}
        base={base}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          setShowCreate(false);
          setRefresh((value) => value + 1);
        }}
      />

      {customers.status === 'loading' ? <LoadingState /> : null}
      {customers.status === 'error' ? (
        <StatePanel
          variant={customers.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={customers.error.status === 403 ? 'دسترسی ندارید' : 'فهرست قابل نمایش نیست'}
        />
      ) : null}

      {customers.status === 'ready' ? (
        <V2DataGrid
          icon="users"
          title="فهرست مشتریان"
          subtitle={
            customers.data.total > 0
              ? `${(offset + 1).toLocaleString('fa-IR')}–${pageEnd.toLocaleString('fa-IR')} از ${customers.data.total.toLocaleString('fa-IR')} مشتری`
              : 'بدون مشتری'
          }
          storageKey="crm-customers"
          search={{
            value: query,
            onChange: (value) => patchParams({ q: value || null, offset: null }),
            placeholder: 'نام یا کد مشتری',
          }}
          filters={[
            {
              id: 'status',
              label: 'وضعیت',
              value: status,
              onChange: (value) => patchParams({ status: value || null, offset: null }),
              options: [
                { value: '', label: 'همه' },
                { value: 'active', label: 'فعال' },
                { value: 'inactive', label: 'غیرفعال' },
              ],
            },
            ...(labelOptions.length > 0
              ? [
                  {
                    id: 'label',
                    label: 'برچسب',
                    value: labelId,
                    onChange: (value: string) =>
                      patchParams({ label: value || null, offset: null }),
                    options: [
                      { value: '', label: 'همه' },
                      ...labelOptions.map((label) => ({ value: label.labelId, label: label.name })),
                    ],
                  },
                ]
              : []),
          ]}
          toolbarExtra={
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <select
                className="sk-select"
                aria-label="مرتب‌سازی"
                style={{ height: 32, width: 150 }}
                value={sort}
                onChange={(event) => patchParams({ sort: event.target.value, offset: null })}
              >
                <option value="code">مرتب‌سازی: کد</option>
                <option value="name">مرتب‌سازی: نام</option>
                <option value="updatedAt">مرتب‌سازی: آخرین تغییر</option>
              </select>
              <select
                className="sk-select"
                aria-label="جهت مرتب‌سازی"
                style={{ height: 32, width: 100 }}
                value={order}
                onChange={(event) => patchParams({ order: event.target.value, offset: null })}
              >
                <option value="asc">صعودی</option>
                <option value="desc">نزولی</option>
              </select>
            </div>
          }
          serverPage={{
            offset,
            limit: customers.data.limit || limit,
            total: customers.data.total,
            onChange: (next) => patchParams({ offset: next > 0 ? String(next) : null }),
          }}
          columns={[
            {
              key: 'ref',
              header: 'کد',
              width: 110,
              render: (row: CustomerSummary) =>
                row.ref ? (
                  <Link
                    to={`${base}/crm/customers/${encodeURIComponent(row.ref)}`}
                    className="ltr sk-num"
                    style={{ fontWeight: 600 }}
                  >
                    {row.ref}
                  </Link>
                ) : (
                  '—'
                ),
            },
            {
              key: 'name',
              header: 'نام مشتری',
              width: 200,
              wrap: true,
              render: (row: CustomerSummary) =>
                row.name ? (
                  <PartyLink base={base} partyRef={row.ref}>
                    {row.name}
                  </PartyLink>
                ) : (
                  <span style={{ fontWeight: 500 }}>—</span>
                ),
            },
            {
              key: 'labels',
              header: 'برچسب‌ها',
              width: 200,
              wrap: true,
              render: (row: CustomerSummary) =>
                row.labels && row.labels.length > 0 ? (
                  <LabelChips labels={row.labels} max={3} />
                ) : (
                  '—'
                ),
            },
            {
              key: 'kind',
              header: 'نوع',
              width: 90,
              muted: true,
              render: (row: CustomerSummary) => (row.kind ? KIND_LABEL[row.kind] : '—'),
            },
            {
              key: 'roles',
              header: 'نقش‌ها',
              width: 150,
              render: (row: CustomerSummary) =>
                row.roles && row.roles.length > 0 ? (
                  <span style={{ display: 'inline-flex', gap: 4 }}>
                    {row.roles.map((role) => (
                      <span key={role} className="sk-tag">
                        {role === 'customer' ? 'مشتری' : 'تأمین‌کننده'}
                      </span>
                    ))}
                  </span>
                ) : (
                  '—'
                ),
            },
            {
              key: 'status',
              header: 'وضعیت',
              width: 100,
              render: (row: CustomerSummary) =>
                row.status === 'active' ? (
                  <V2Badge tone="success">فعال</V2Badge>
                ) : row.status === 'inactive' ? (
                  <V2Badge tone="neutral">غیرفعال</V2Badge>
                ) : (
                  '—'
                ),
            },
            {
              key: 'updatedAt',
              header: 'آخرین تغییر',
              width: 110,
              render: (row: CustomerSummary) => (
                <span className="sk-num" style={{ color: 'var(--sk-fg-muted)' }}>
                  {row.updatedAt ? displayJalaliDate(row.updatedAt.slice(0, 10)) : '—'}
                </span>
              ),
            },
          ]}
          rows={customers.data.items}
          rowKey={(row) => row.ref ?? `${row.name ?? ''}:${row.createdAt ?? ''}`}
          rowTone={(row) => (row.status === 'inactive' ? 'neutral' : undefined)}
          legend={[{ tone: 'neutral', label: 'غیرفعال' }]}
          emptyState={
            <V2EmptyState
              icon="users"
              title={
                query || status || labelId
                  ? 'مشتری‌ای با این فیلتر پیدا نشد'
                  : 'هنوز مشتری‌ای ثبت نشده'
              }
              description={
                query || status || labelId ? undefined : 'با «مشتری جدید» اولین مشتری را ثبت کنید.'
              }
            />
          }
        />
      ) : null}
    </div>
  );
}
